import { Box, Button, Header, Loader, Table, Text } from '@profitowi/component-library';
import { AxiosError, AxiosResponse } from 'axios';
import useAgentId from 'pages/Agent/useAgentId';
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { SortingRule } from 'react-table';

import ErrorMessages from 'components/ErrorMessages/ErrorMessages';
import { DEBTS } from 'constants/queries/debts';
import { usePagination } from 'hooks/usePagination';
import { deleteAgentDebt, getDebtsForAgent } from 'services/debt';
import { Debt } from 'types/debt';
import { Page } from 'types/request';
import { decodeUriSortParams } from 'utils/table';

import DebtForm from './DebtForm/DebtForm';
import { createColumn } from './columns';

const AgentDebts = () => {
  const [visible, setVisible] = useState(false);
  const pagination = usePagination(10);
  const navigate = useNavigate();
  const agentId = useAgentId();
  const [sortBy, setSortBy] = useState<Array<SortingRule<any>>>([]);
  const handleSortBy = useCallback((sortBy: SortingRule<any>[]) => setSortBy(sortBy), []);

  const { data, isLoading, isSuccess, isError, error, isFetching, refetch } = useQuery<
    Page<Debt>,
    AxiosError
  >(
    [DEBTS.DEBTS, pagination.currentPage, pagination.perPage, sortBy],
    () =>
      getDebtsForAgent(
        pagination.currentPage,
        pagination.perPage,
        agentId,
        decodeUriSortParams(sortBy)
      ),
    { keepPreviousData: true }
  );

  const { mutate } = useMutation<AxiosResponse, AxiosError, number>(
    (agentAdvanceId) => deleteAgentDebt(agentId, agentAdvanceId),
    {
      onSuccess: () => {
        refetch();
      },
    }
  );

  return (
    <>
      <Box className="space-y-6">
        <div className="flex justify-between">
          <Header as="h4" size="lg" weight="semibold">
            Zadłużenia
          </Header>
          <Button variant="outline-primary" className="px-4 py-1" onPress={() => setVisible(true)}>
            <Text weight="semibold">
              Dodaj
              <i className="bi bi-plus-lg ml-2"></i>
            </Text>
          </Button>
        </div>

        {isError && <ErrorMessages error={error} />}
        {isLoading && <Loader className="h-12 w-12" />}
        {isSuccess && data ? (
          <Box.FullWidth>
            <Table
              pagination={pagination}
              totalPages={data.totalPages}
              data={data.content}
              columns={createColumn(agentId, navigate, mutate)}
              onSortBy={handleSortBy}
              sortBy={sortBy}
              isLoading={isFetching}
            />
          </Box.FullWidth>
        ) : (
          <Loader className="h-12 w-12" />
        )}
      </Box>

      {visible && <DebtForm visible={visible} refetch={refetch} setVisible={setVisible} />}
    </>
  );
};

export default AgentDebts;
